import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { measureConnectionTier, toDisplayTier } from '@/lib/connectionQuality';
import { readCriticalBackupRedirectUrl } from '@/lib/criticalBackupRedirect';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useWebinarT } from '@/hooks/useWebinarT';
import { useWebinarLng } from '@/contexts/WebinarLanguageContext';
import {
  fetchMyPublicQuizSubmission,
  fetchMyPublicSurveySubmission,
  fetchPublicWaitingRoomPage,
  fetchPublicWaitingRoomQuiz,
  fetchPublicWaitingRoomSurvey,
  fetchPublicWebinarIndex,
  fetchPublicWebinarSession,
} from '@/features/webinar/webinarThunks';
import type { RootState } from '@/app/store';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { PoweredByLogo } from '@/components/pages/public/PoweredByLogo';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Check,
  Clock,
  Loader2,
  Plus,
  Wifi,
  WifiHigh,
  WifiLow,
} from 'lucide-react';
import IconFlash from '@/components/icons/IconFlash';
import IconNote from '@/components/icons/IconNote';
import { DateTime } from 'luxon';
import CurvedCorner from '@/components/ui/CurvedCorner';
import WaitingRoomSurveyForm from '@/components/pages/waitingRoom/WaitingRoomSurveyForm';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { fetchAttendeeInfo, postAnalyticsEvent } from '@/features/live/liveThunks';
import WaitingRoomQuizForm from '@/components/pages/waitingRoom/WaitingRoomQuizForm';
import SplashScreen from '@/components/common/SplashScreen';
import LiveEntryError from '@/components/common/LiveEntryError';
import { reportPublicSessionUnavailable } from '@/lib/publicSessionRead';
import { useChannelName } from '@/routes/AblyAuth';
import { useChannel } from 'ably/react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getFormattedDate } from '@/lib/utils';
import {
  getCountdownDisplay,
  isSingleDayCountdown,
  type CountdownParts,
} from '@/lib/waitingRoomCountdown';

const toolsData = [
  {
    id: 1,
    img: '/assets/tools-1.png',
  },
  {
    id: 2,
    img: '/assets/tools-2.png',
  },
  {
    id: 3,
    img: '/assets/tools-3.png',
  },
];

type CountdownValue = CountdownParts;

const useCountdown = (targetDate: string | undefined): CountdownValue => {
  const calculate = (): CountdownValue => {
    if (!targetDate) return null;
    const now = DateTime.now().toUTC();
    const target = DateTime.fromISO(targetDate, { zone: 'utc' });
    if (!target.isValid) return null;
    const diff = target.diff(now, ['days', 'hours', 'minutes', 'seconds']);
    if (diff.days < 0 || diff.hours < 0 || diff.minutes < 0 || diff.seconds < 0) return null;
    return {
      days: Math.floor(diff.days),
      hours: Math.floor(diff.hours),
      minutes: Math.floor(diff.minutes).toString().padStart(2, '0'),
      seconds: Math.floor(diff.seconds).toString().padStart(2, '0'),
    };
  };

  const [countdown, setCountdown] = useState<CountdownValue>(calculate);

  useEffect(() => {
    if (!targetDate) {
      setCountdown(null);
      return;
    }
    setCountdown(calculate());
    const interval = setInterval(() => setCountdown(calculate()), 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetDate]);

  return countdown;
};

type WaitingRoomJwtPayload = {
  firstName?: string;
};

function getFirstNameFromJwtToken(token: string | null): string | undefined {
  if (!token) return undefined;
  try {
    const decoded = jwtDecode<WaitingRoomJwtPayload>(token);
    const name = decoded.firstName;
    if (typeof name === 'string' && name.trim() !== '') {
      return name.trim();
    }
  } catch {}
  return undefined;
}

function parseTotalWaitersFromWaitingRoomUpdate(data: unknown): number | null {
  if (data == null || typeof data !== 'object') {
    return null;
  }
  const record = data as Record<string, unknown>;
  const raw = record.totalWaiters ?? record.total_waiters;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) {
    return Math.floor(raw);
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed >= 0) {
      return Math.floor(parsed);
    }
  }
  return null;
}

function parseNamesPoolFromWaitingRoomUpdate(data: unknown): string[] {
  if (data == null || typeof data !== 'object') {
    return [];
  }
  const record = data as Record<string, unknown>;
  const raw = record.namesPool ?? record.names_pool;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter((n): n is string => typeof n === 'string' && n.trim() !== '')
    .map((n) => n.trim());
}

function parseWaitingRoomUpdatePayload(data: unknown): {
  totalWaiters: number | null;
  namesPool: string[];
} {
  return {
    totalWaiters: parseTotalWaitersFromWaitingRoomUpdate(data),
    namesPool: parseNamesPoolFromWaitingRoomUpdate(data),
  };
}

function shuffleArrayCopy<T>(items: readonly T[]): T[] {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function useIsSmUp() {
  const [smUp, setSmUp] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 640px)').matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const handler = () => setSmUp(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return smUp;
}

export default function WaitingRoomPage() {
  const { webinarId, sessionIdentifier } = useParams<{
    webinarId: string;
    sessionIdentifier: string;
  }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useWebinarT('webinars');
  const webinarLng = useWebinarLng();
  const searchParams = useSearchParams();
  const token = searchParams[0].get('token');
  const isSmUp = useIsSmUp();
  const webinarIndex = useAppSelector(
    (state: RootState) => state.webinar.currentPublicWebinarIndex,
  );

  const publicSession = useAppSelector((state: RootState) => state.webinar.currentPublicSession);
  const publicSessionError = useAppSelector((state: RootState) => state.webinar.publicSessionError);
  const publicSessionLoading = useAppSelector(
    (state: RootState) => state.webinar.publicSessionLoading,
  );
  const publicSessionErrorTransient = useAppSelector(
    (state: RootState) => state.webinar.publicSessionErrorTransient,
  );
  const waitingRoomPage = useAppSelector(
    (state: RootState) => state.webinar.currentPublicWaitingRoomPage,
  );
  const waitingRoomSurvey = useAppSelector(
    (state: RootState) => state.webinar.currentPublicWaitingRoomSurvey,
  );
  const currentPublicSurveySubmission = useAppSelector(
    (state: RootState) => state.webinar.currentPublicSurveySubmission,
  );
  const waitingRoomQuiz = useAppSelector(
    (state: RootState) => state.webinar.currentPublicWaitingRoomQuiz,
  );
  const currentPublicQuizSubmission = useAppSelector(
    (state: RootState) => state.webinar.currentPublicQuizSubmission,
  );
  const publicWaitingRoomSurveyLoading = useAppSelector(
    (state: RootState) => state.webinar.publicWaitingRoomSurveyLoading,
  );
  const publicWaitingRoomQuizLoading = useAppSelector(
    (state: RootState) => state.webinar.publicWaitingRoomQuizLoading,
  );
  const publicSurveySubmissionLoading = useAppSelector(
    (state: RootState) => state.webinar.publicSurveySubmissionLoading,
  );
  const publicQuizSubmissionLoading = useAppSelector(
    (state: RootState) => state.webinar.publicQuizSubmissionLoading,
  );
  const publicWaitingRoomSurveyError = useAppSelector(
    (state: RootState) => state.webinar.publicWaitingRoomSurveyError,
  );
  const publicWaitingRoomQuizError = useAppSelector(
    (state: RootState) => state.webinar.publicWaitingRoomQuizError,
  );
  const attendeeInfo = useAppSelector((state: RootState) => state.live.attendeeInfo);
  const attendeeFirstNameFromJwt = useMemo(() => getFirstNameFromJwtToken(token), [token]);
  const displayAttendeeFirstName = attendeeFirstNameFromJwt ?? attendeeInfo?.firstName;

  const hasWaitingRoomSurveyCompleted = useMemo(() => {
    if (!waitingRoomSurvey?.id || !currentPublicSurveySubmission) return false;
    return (
      currentPublicSurveySubmission.templateId === waitingRoomSurvey.id &&
      (currentPublicSurveySubmission.answers?.length ?? 0) > 0
    );
  }, [waitingRoomSurvey?.id, currentPublicSurveySubmission]);

  const hasWaitingRoomQuizCompleted = useMemo(() => {
    if (!waitingRoomQuiz?.id || !currentPublicQuizSubmission) return false;
    return (
      currentPublicQuizSubmission.templateId === waitingRoomQuiz.id &&
      (currentPublicQuizSubmission.answers?.length ?? 0) > 0
    );
  }, [waitingRoomQuiz?.id, currentPublicQuizSubmission]);

  useEffect(() => {
    if (webinarId && sessionIdentifier && !publicSessionLoading && !publicSessionError) {
      dispatch(
        fetchPublicWebinarSession({
          webinarId: Number(webinarId),
          sessionIdentifier,
        }),
      );

      if (!token) {
        return;
      }
      dispatch(
        fetchPublicWebinarIndex({
          webinarId: Number(webinarId),
          authorization: token,
        }),
      );
      dispatch(
        fetchPublicWaitingRoomPage({
          webinarId: Number(webinarId),
          authorization: token,
        }),
      );
      if (!getFirstNameFromJwtToken(token)) {
        dispatch(fetchAttendeeInfo({ webinarId: Number(webinarId), authorization: token }));
      }
    }
  }, [webinarId, sessionIdentifier, token, dispatch]);

  useEffect(() => {
    if (!webinarId || !token || !waitingRoomPage) {
      return;
    }

    const interactionType = waitingRoomPage.interactionType;

    if (interactionType === 'SURVEY') {
      dispatch(
        fetchPublicWaitingRoomSurvey({
          webinarId: Number(webinarId),
          authorization: token,
        }),
      );
    } else if (interactionType === 'QUIZ') {
      dispatch(
        fetchPublicWaitingRoomQuiz({
          webinarId: Number(webinarId),
          authorization: token,
        }),
      );
    }
  }, [webinarId, token, waitingRoomPage?.interactionType, dispatch]);

  const [waitingRoomRealtime, setWaitingRoomRealtime] = useState<{
    totalWaiters: number;
    namesPool: string[];
  } | null>(null);

  useEffect(() => {
    setWaitingRoomRealtime(null);
  }, [webinarId, sessionIdentifier]);

  useEffect(() => {
    if (!webinarId || !token) {
      return;
    }

    const interactionType = waitingRoomPage?.interactionType;

    if (interactionType === 'SURVEY' && waitingRoomSurvey?.id) {
      dispatch(
        fetchMyPublicSurveySubmission({
          webinarId: Number(webinarId),
          templateId: Number(waitingRoomSurvey.id),
          authorization: token,
        }),
      );
    } else if (interactionType === 'QUIZ' && waitingRoomQuiz?.id) {
      dispatch(
        fetchMyPublicQuizSubmission({
          webinarId: Number(webinarId),
          templateId: Number(waitingRoomQuiz.id),
          authorization: token,
        }),
      );
    }
    // Si interactionType === 'NONE', on ne fait rien
  }, [
    webinarId,
    token,
    waitingRoomPage?.interactionType,
    waitingRoomSurvey?.id,
    waitingRoomQuiz?.id,
    dispatch,
  ]);
  const channelName = useChannelName();

  const onWaitingRoomChannelMessage = useCallback(
    (message: { name?: string; data?: unknown }) => {
      if (!channelName || !message) return;

      const normalizedMessages =
        message.name === 'batch-events' && Array.isArray(message.data)
          ? message.data.filter(
              (event): event is { name: string; data: unknown } =>
                !!event &&
                typeof event === 'object' &&
                typeof (event as { name?: string }).name === 'string',
            )
          : [{ name: message.name, data: message.data }];

      for (const currentMessage of normalizedMessages) {
        const data: unknown = currentMessage.data;

        // Même filet que sur la page de live : la salle d'attente ne lisait l'URL de secours qu'au
        // chargement, donc un participant déjà présent n'était jamais emmené.
        const criticalRedirectUrl = readCriticalBackupRedirectUrl(currentMessage.name, data);
        if (criticalRedirectUrl) {
          window.location.replace(criticalRedirectUrl);
          return;
        }

        const isSessionUpdated =
          (currentMessage.name === 'live-event' &&
            data &&
            typeof data === 'object' &&
            (data as { eventType?: string }).eventType === 'SESSION_UPDATED') ||
          (currentMessage.name === 'event-message' &&
            data &&
            typeof data === 'object' &&
            (data as { type?: string }).type === 'SESSION_UPDATE');

        if (isSessionUpdated && webinarId && sessionIdentifier) {
          dispatch(
            fetchPublicWebinarSession({
              webinarId: Number(webinarId),
              sessionIdentifier,
            }),
          );
        }

        if (currentMessage.name === 'waiting-room-update') {
          const { totalWaiters, namesPool } = parseWaitingRoomUpdatePayload(data);
          if (totalWaiters !== null) {
            setWaitingRoomRealtime({ totalWaiters, namesPool });
          }
        }
      }
    },
    [channelName, webinarId, sessionIdentifier, dispatch],
  );

  useChannel(channelName || '', onWaitingRoomChannelMessage);
  useEffect(() => {
    const isInProgress = publicSession?.session?.status === 'IN_PROGRESS';

    if (!isInProgress || !webinarId || !sessionIdentifier || !token) {
      return;
    }

    navigate(`/live/${webinarId}/${sessionIdentifier}?token=${token}`, {
      replace: true,
    });
  }, [publicSession?.session?.status, webinarId, sessionIdentifier, token, navigate]);

  useEffect(() => {
    async function sendInfo() {
      const connection = await measureConnectionTier();
      if (connection !== null) setConnectionQuality(toDisplayTier(connection));
    }
    sendInfo();
    const interval = setInterval(() => {
      sendInfo();
    }, 20000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    if (!webinarId || !sessionIdentifier || !token) {
      return;
    }

    const sendHeartbeat = () => {
      dispatch(
        postAnalyticsEvent({
          webinarId: Number(webinarId),
          sessionIdentifier,
          authorization: token,
          input: {
            eventType: 'WAITING_ROOM_HEARTBEAT',
            payloadJson: JSON.stringify({ timeNow: Date.now() }),
            occurredAt: new Date().toISOString(),
          },
        }),
      );
    };

    sendHeartbeat();
    const intervalId = setInterval(sendHeartbeat, 10000);

    return () => {
      clearInterval(intervalId);
    };
  }, [webinarId, sessionIdentifier, token, dispatch]);
  const [isToolsActive, setIsToolsActive] = useState(false);
  const [isSurveyActive, setIsSurveyActive] = useState(false);
  const [isQuizActive, setIsQuizActive] = useState(false);
  const [toolsStep, setToolsStep] = useState(1);
  const [connectionQuality, setConnectionQuality] = useState<'high' | 'medium' | 'low'>('high');
  const waitingRoomAutoLayoutAppliedRef = useRef(false);
  const prevQuizSubmissionLoadingRef = useRef(publicQuizSubmissionLoading);
  const prevSurveySubmissionLoadingRef = useRef(publicSurveySubmissionLoading);

  useEffect(() => {
    waitingRoomAutoLayoutAppliedRef.current = false;
  }, [webinarId, sessionIdentifier]);

  useEffect(() => {
    if (prevQuizSubmissionLoadingRef.current && !publicQuizSubmissionLoading) {
      waitingRoomAutoLayoutAppliedRef.current = false;
    }
    prevQuizSubmissionLoadingRef.current = publicQuizSubmissionLoading;
  }, [publicQuizSubmissionLoading]);

  useEffect(() => {
    if (prevSurveySubmissionLoadingRef.current && !publicSurveySubmissionLoading) {
      waitingRoomAutoLayoutAppliedRef.current = false;
    }
    prevSurveySubmissionLoadingRef.current = publicSurveySubmissionLoading;
  }, [publicSurveySubmissionLoading]);

  useEffect(() => {
    if (!waitingRoomPage || waitingRoomAutoLayoutAppliedRef.current) {
      return;
    }

    const hasMainMedia = !!(
      (waitingRoomPage.mainImage && String(waitingRoomPage.mainImage).trim()) ||
      (waitingRoomPage.mainVideoUrl && String(waitingRoomPage.mainVideoUrl).trim())
    );

    // Quand aucune interaction n'est ouverte automatiquement (aucune configurée,
    // déjà répondue, ou absente) : on garde le média principal s'il existe,
    // sinon on bascule sur l'onglet "outils".
    const applyFallbackLayout = () => {
      setIsSurveyActive(false);
      setIsQuizActive(false);
      setIsToolsActive(!hasMainMedia);
    };

    const interaction = waitingRoomPage.interactionType;

    if (interaction === 'SURVEY') {
      if (!token) {
        return;
      }
      if (publicWaitingRoomSurveyLoading) {
        return;
      }
      if (!waitingRoomSurvey && !publicWaitingRoomSurveyError) {
        return;
      }
      if (waitingRoomSurvey?.id && publicSurveySubmissionLoading) {
        return;
      }

      const hasWrSurvey =
        !!waitingRoomSurvey?.id &&
        waitingRoomSurvey.waitingRoomSurvey !== false &&
        !publicWaitingRoomSurveyError;

      waitingRoomAutoLayoutAppliedRef.current = true;
      if (hasWrSurvey && !hasWaitingRoomSurveyCompleted) {
        setIsSurveyActive(true);
        setIsQuizActive(false);
        setIsToolsActive(false);
      } else {
        applyFallbackLayout();
      }
      return;
    }

    if (interaction === 'QUIZ') {
      if (!token) {
        return;
      }
      if (publicWaitingRoomQuizLoading) {
        return;
      }
      if (!waitingRoomQuiz && !publicWaitingRoomQuizError) {
        return;
      }
      if (waitingRoomQuiz?.id && publicQuizSubmissionLoading) {
        return;
      }

      const hasWrQuiz =
        !!waitingRoomQuiz?.id &&
        waitingRoomQuiz.waitingRoomQuiz !== false &&
        !publicWaitingRoomQuizError;

      waitingRoomAutoLayoutAppliedRef.current = true;
      if (hasWrQuiz && !hasWaitingRoomQuizCompleted) {
        setIsQuizActive(true);
        setIsSurveyActive(false);
        setIsToolsActive(false);
      } else {
        applyFallbackLayout();
      }
      return;
    }
    waitingRoomAutoLayoutAppliedRef.current = true;
    applyFallbackLayout();
  }, [
    token,
    waitingRoomPage?.webinarId,
    waitingRoomPage?.interactionType,
    waitingRoomPage?.mainImage,
    waitingRoomPage?.mainVideoUrl,
    hasWaitingRoomSurveyCompleted,
    hasWaitingRoomQuizCompleted,
    publicWaitingRoomSurveyLoading,
    publicWaitingRoomQuizLoading,
    publicWaitingRoomSurveyError,
    publicWaitingRoomQuizError,
    publicSurveySubmissionLoading,
    publicQuizSubmissionLoading,
    waitingRoomSurvey?.id,
    waitingRoomSurvey?.waitingRoomSurvey,
    waitingRoomQuiz?.id,
    waitingRoomQuiz?.waitingRoomQuiz,
  ]);

  useEffect(() => {
    const interaction = waitingRoomPage?.interactionType;
    if (!interaction || interaction === 'NONE') {
      return;
    }
    // Une fois l'interaction répondue : on garde le média principal s'il existe,
    // sinon on bascule sur l'onglet "outils".
    const hasMainMedia = !!(
      (waitingRoomPage?.mainImage && String(waitingRoomPage.mainImage).trim()) ||
      (waitingRoomPage?.mainVideoUrl && String(waitingRoomPage.mainVideoUrl).trim())
    );
    if (interaction === 'QUIZ' && hasWaitingRoomQuizCompleted) {
      setIsQuizActive(false);
      setIsSurveyActive(false);
      setIsToolsActive(!hasMainMedia);
    }
    if (interaction === 'SURVEY' && hasWaitingRoomSurveyCompleted) {
      setIsSurveyActive(false);
      setIsQuizActive(false);
      setIsToolsActive(!hasMainMedia);
    }
  }, [
    waitingRoomPage?.interactionType,
    waitingRoomPage?.mainImage,
    waitingRoomPage?.mainVideoUrl,
    hasWaitingRoomQuizCompleted,
    hasWaitingRoomSurveyCompleted,
  ]);

  const countdown = useCountdown(publicSession?.session.utcPlannedDate);

  const { timeLeftDisplay, remainingLabel } = useMemo(() => {
    const display = getCountdownDisplay(countdown);
    const remainingLabel = t(
      isSingleDayCountdown(display)
        ? 'waitingRoom.publicPage.countdown.remainingShortSingular'
        : 'waitingRoom.publicPage.countdown.remainingShort',
    );
    if (display.kind === 'withDays') {
      return {
        timeLeftDisplay: t('waitingRoom.publicPage.countdown.remaining', {
          count: display.days,
          time: display.time,
        }),
        remainingLabel,
      };
    }
    return { timeLeftDisplay: display.time, remainingLabel };
  }, [countdown, t]);

  const containerBackgroundStyle = useMemo(() => {
    const backgroundType = waitingRoomPage?.background ?? 'DEFAULT';
    const backgroundColor = waitingRoomPage?.backgroundColor;
    const backgroundGradient = waitingRoomPage?.backgroundGradient;
    const backgroundImageUrl = waitingRoomPage?.backgroundImageUrl || undefined;

    if (backgroundType === 'IMAGE') {
      const decodedUrl = backgroundImageUrl ? decodeURIComponent(backgroundImageUrl) : undefined;
      return {
        backgroundColor: publicSession?.visualIdentity?.backgroundColor || '#FBFBFB',
        backgroundImage: decodedUrl ? `url(${decodedUrl})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      } as React.CSSProperties;
    }

    if (backgroundType === 'GRADIENT' && publicSession?.visualIdentity) {
      if (backgroundGradient === '2') {
        return {
          background: `linear-gradient(226deg, ${publicSession.visualIdentity.gradient2Color1 || '#000'} 0.2%, ${publicSession.visualIdentity.gradient2Color2 || '#fff'} 104.63%)`,
        } as React.CSSProperties;
      }

      return {
        background: `linear-gradient(to bottom, ${publicSession.visualIdentity.gradient1Color1 || '#000'}, ${publicSession.visualIdentity.gradient1Color2 || '#fff'})`,
      } as React.CSSProperties;
    }

    if (backgroundType === 'DEFAULT' && publicSession?.visualIdentity) {
      return {
        backgroundColor: publicSession.visualIdentity.backgroundColor || '#FBFBFB',
      } as React.CSSProperties;
    }

    return {
      backgroundColor:
        (backgroundColor && backgroundColor.trim().length > 0
          ? backgroundColor
          : publicSession?.visualIdentity?.backgroundColor) || '#FBFBFB',
    } as React.CSSProperties;
  }, [waitingRoomPage, publicSession?.visualIdentity]);

  const displayNamesSample = useMemo(() => {
    const pool = waitingRoomRealtime?.namesPool ?? [];
    const myFirstName = (displayAttendeeFirstName ?? '').trim().toLowerCase();
    const normalize = (n: string) =>
      (typeof n === 'string' ? n.trim() : '').replace(/^\w/, (c) => c.toUpperCase());
    let removed = false;
    const others = pool
      .map((n) => (typeof n === 'string' ? n.trim() : ''))
      .filter((n) => {
        if (!n) return false;
        const key = n.toLowerCase();
        if (key === myFirstName && !removed) {
          removed = true;
          return false;
        }
        return true;
      })
      .map(normalize);

    const countByName = new Map<string, number>();
    for (const n of others) {
      const key = n.toLowerCase();
      countByName.set(key, (countByName.get(key) ?? 0) + 1);
    }
    const uniqueNames = [...new Set(others)];
    const duplicatedNames = uniqueNames.filter((n) => (countByName.get(n.toLowerCase()) ?? 0) > 1);

    let selection: string[] = [];
    if (duplicatedNames.length > 0) {
      const pick = duplicatedNames[Math.floor(Math.random() * duplicatedNames.length)];
      selection.push(pick);
    }
    const remaining = uniqueNames.filter(
      (n) => n.toLowerCase() !== (selection[0]?.toLowerCase() ?? ''),
    );
    const shuffled = shuffleArrayCopy(remaining);
    selection.push(...shuffled.slice(0, 3 - selection.length));
    return selection;
  }, [waitingRoomRealtime?.namesPool, displayAttendeeFirstName]);

  const renderWifiIcon = () => {
    const iconProps = { size: 24 };
    switch (connectionQuality) {
      case 'high':
        return <Wifi {...iconProps} color="green" />;
      case 'medium':
        return <WifiHigh {...iconProps} color="green" />;
      case 'low':
        return <WifiLow {...iconProps} color="red" />;
      default:
        return <Loader2 {...iconProps} color="black" className="animate-spin" />;
    }
  };

  const publicSessionUnavailable =
    !publicSessionLoading && Boolean(publicSessionError) && Boolean(publicSessionErrorTransient);

  useEffect(() => {
    if (!publicSessionUnavailable) return;
    reportPublicSessionUnavailable({
      page: 'waiting-room',
      webinarId,
      sessionIdentifier,
      error: publicSessionError ?? null,
    });
  }, [publicSessionUnavailable, webinarId, sessionIdentifier, publicSessionError]);

  const retryPublicSession = useCallback(() => {
    if (!webinarId || !sessionIdentifier) return;
    dispatch(fetchPublicWebinarSession({ webinarId: Number(webinarId), sessionIdentifier }));
  }, [webinarId, sessionIdentifier, dispatch]);

  const isLoading =
    !publicSession?.session ||
    !publicSession?.visualIdentity ||
    (!attendeeInfo && !attendeeFirstNameFromJwt);

  if (
    (!publicSessionLoading && publicSessionError && !publicSessionErrorTransient) ||
    publicSession?.session?.status === 'FINISHED'
  ) {
    if (publicSession?.session?.criticalBackupUrl) {
      window.location.replace(publicSession.session?.criticalBackupUrl);
    }
    return <SplashScreen label={t('waitingRoom.publicPage.sessionClosed')} fullscreen />;
  }

  if (publicSessionUnavailable) {
    return <LiveEntryError step="session" onRetry={retryPublicSession} />;
  }

  if (isLoading) {
    return <SplashScreen label={t('live.loading')} />;
  }
  const timeRemaining = () => (
    <>
      {/* time remaining */}
      <div
        className="w-full flex flex-col items-center  gap-2 p-4 rounded-2xl border "
        style={{
          backgroundColor:
            publicSession?.visualIdentity?.theme === 'LIGHT'
              ? 'rgba(31, 31, 31, 0.03)'
              : 'rgba(255, 255, 255, 0.1)',

          borderColor: publicSession?.visualIdentity?.contrastColor,
        }}
      >
        <div className="flex items-center justify-center flex-wrap gap-1 w-full">
          <Clock className="size-4 md:size-6 shrink-0 text-[#9E9FA6]" />
          <div
            className={`text-[13px] sm:text-sm font-bold text-center ${publicSession?.visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'}`}
            style={{
              fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
              color: publicSession?.visualIdentity?.theme === 'DARK' ? '#929185' : '#6D6E7A',
            }}
          >
            {(() => {
              const formatted = getFormattedDate(
                publicSession?.session.utcPlannedDate,
                publicSession?.session.timezone,
                webinarLng,
              );
              return formatted.charAt(0).toUpperCase() + formatted.slice(1);
            })()}
          </div>
        </div>

        <div className="flex flex-col items-center gap-2 w-full">
          <div
            className="text-2xl sm:text-[32px] font-semibold text-center"
            style={{
              fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
              color: publicSession?.visualIdentity?.accentColor,
            }}
          >
            {timeLeftDisplay}
          </div>
          <p
            className="text-sm text-center"
            style={{
              fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
              color: publicSession?.visualIdentity?.theme === 'DARK' ? '#929185' : '#6D6E7A',
            }}
          >
            {remainingLabel}
          </p>
        </div>

        {!countdown || publicSession?.session?.status === 'IN_PROGRESS' ? (
          <p
            className="text-xl font-medium text-center"
            style={{
              fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
              color: publicSession?.visualIdentity?.theme === 'LIGHT' ? '#2C6CFF' : '#95B6FF',
            }}
          >
            {t('waitingRoom.publicPage.countdown.liveStartsSoon')}
          </p>
        ) : (
          <p
            className="text-xs text-center flex items-center gap-1 flex-wrap justify-center"
            style={{
              fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
              color: publicSession?.visualIdentity?.theme === 'LIGHT' ? '#17805C' : '#C0FFEA',
            }}
          >
            {waitingRoomPage?.displayParticipantsCountEnabled &&
              waitingRoomRealtime !== null &&
              waitingRoomRealtime.totalWaiters >
                (waitingRoomPage?.displayParticipantsMinThreshold ?? 0) &&
              (() => {
                const count = waitingRoomRealtime.totalWaiters;
                const othersCount = Math.max(0, count - 1);

                if (count <= 1) {
                  return t('waitingRoom.publicPage.countdown.attendeeNoOne');
                }

                const selectedNames = displayNamesSample;
                if (selectedNames.length > 0) {
                  const namesStr = selectedNames.join(', ');
                  const displayedOthersCount = Math.max(0, othersCount - selectedNames.length);
                  if (displayedOthersCount === 0) {
                    return t('waitingRoom.publicPage.countdown.attendeesWithNamesOnly', {
                      names: namesStr,
                      count: selectedNames.length,
                    });
                  }
                  return displayedOthersCount === 1
                    ? t('waitingRoom.publicPage.countdown.attendeesWithNamesAndOne', {
                        names: namesStr,
                      })
                    : t('waitingRoom.publicPage.countdown.attendeesWithNamesAndOthers', {
                        names: namesStr,
                        count: displayedOthersCount,
                      });
                }

                const label =
                  othersCount > 2
                    ? t('waitingRoom.publicPage.countdown.attendees_other')
                    : t('waitingRoom.publicPage.countdown.attendees_one');
                const num = othersCount > 0 ? String(othersCount) : '';
                return `${num} ${label}`;
              })()}
          </p>
        )}
      </div>
    </>
  );
  const toolsContent = () => {
    return toolsData
      .filter((tool) => tool.id === toolsStep)
      .map((tool) => (
        <div
          key={tool.id}
          className="flex flex-col items-center justify-between h-full gap-4 w-full"
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center">
              {tool.id > 1 && (
                <ArrowLeftIcon
                  className="size-6 text-[#9E9FA6] cursor-pointer mr-2"
                  onClick={() => {
                    setToolsStep(tool.id - 1);
                  }}
                />
              )}
              <h3
                className="text-xl font-medium"
                style={{
                  color: publicSession?.visualIdentity?.theme === 'DARK' ? 'white' : 'black',
                  fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
                }}
              >
                <span
                  style={{
                    color: publicSession?.visualIdentity?.theme === 'DARK' ? '#95B6FF' : '#2C6CFF',
                    fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
                  }}
                >
                  {tool.id}/3
                </span>{' '}
                {t(`waitingRoom.publicPage.tools.title${tool.id}` as const)}
              </h3>
            </div>
            {tool.id < 3 && (
              <ArrowRightIcon
                className="size-6 text-[#9E9FA6] cursor-pointer"
                onClick={() => {
                  setToolsStep(tool.id + 1);
                }}
              />
            )}
          </div>
          <p
            className="text-sm"
            style={{
              color: publicSession?.visualIdentity?.theme === 'DARK' ? 'white' : '#9E9FA6',
              fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
            }}
          >
            {t(`waitingRoom.publicPage.tools.description${tool.id}` as const)}
          </p>

          <img
            src={tool.img}
            alt={t(`waitingRoom.publicPage.tools.title${tool.id}` as const)}
            className="w-full h-full object-contain"
          />
        </div>
      ));
  };

  return (
    <div
      className="  w-full h-full  min-h-svh relative flex items-center justify-between p-4 sm:p-12 lg:p-8"
      style={containerBackgroundStyle}
    >
      <div className="w-full h-full max-w-[1440px]  mx-auto flex flex-col lg:flex-row items-start gap-12 lg:gap-41 justify-between ">
        {/* left side */}

        <div className="w-full flex flex-1 flex-col gap-8">
          {publicSession?.visualIdentity?.logoUrl && waitingRoomPage?.displayLogo && (
            <img
              src={publicSession?.visualIdentity?.logoUrl}
              alt="logo Welya"
              width={140}
              height={64}
              className="object-contain mx-auto lg:mx-0"
            />
          )}
          <div className="w-full block lg:hidden ">{timeRemaining()}</div>
          <h1
            className={`text-[32px] font-semibold text-left  ${
              publicSession?.visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'
            }`}
            style={{
              fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
            }}
          >
            {t('waitingRoom.publicPage.greeting')}{' '}
            <span
              className="inline-block"
              style={{
                fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
                color: publicSession?.visualIdentity?.accentColor,
              }}
            >
              {displayAttendeeFirstName}
              {t('waitingRoom.publicPage.greetingEnd')}
            </span>
          </h1>

          <div className="flex flex-col gap-4 items-start justify-center">
            <h2
              aria-label={t('waitingRoom.publicPage.aria.webinarTitle')}
              title={t('waitingRoom.publicPage.aria.webinarTitle')}
              className={`text-2xl font-bold ${publicSession?.visualIdentity.theme === 'DARK' ? 'text-white' : 'text-black'}`}
              style={{
                fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
              }}
            >
              {publicSession?.session.webinarTitle}
            </h2>
            <p
              className={`text-sm font-normal ${publicSession?.visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'}`}
              style={{
                fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
              }}
            >
              {publicSession?.session.webinarDescription}
            </p>
            {waitingRoomPage?.displaySpeakers && (
              <div className="flex flex-col gap-4">
                {publicSession?.session.presenters?.map((presenter, index) => (
                  <div key={presenter?.fullname + index} className="flex items-center gap-4">
                    <Avatar className="size-12">
                      <AvatarImage src={presenter.pictureUrl} className="object-cover" />
                      <AvatarFallback>
                        {presenter.fullname
                          ?.split(' ')
                          .map((n) => n.charAt(0))
                          .join('')
                          .toUpperCase()
                          .slice(0, 2) ?? '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p
                        className={
                          publicSession?.visualIdentity?.theme === 'DARK'
                            ? 'text-white'
                            : 'text-black'
                        }
                        style={{
                          fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
                        }}
                      >
                        {t('waitingRoom.publicPage.presenterBy')}
                        <span
                          className=" text-xl font-semibold"
                          style={{
                            fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
                          }}
                        >
                          {presenter.fullname}
                        </span>{' '}
                      </p>
                      <p
                        className={`text-sm ${publicSession?.visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'}`}
                        style={{
                          fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
                        }}
                      >
                        {presenter.job === 'Host' || presenter.job === 'Présentateur'
                          ? t('waitingRoom.publicPage.presenterLabel')
                          : presenter.job}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {waitingRoomPage?.displayGuests &&
              (publicSession?.session.speakers?.length ?? 0) > 0 && (
                <div className="flex flex-col gap-4">
                  {publicSession?.session.speakers?.map((speaker, index) => (
                    <div key={speaker?.fullname + index} className="flex items-center gap-4">
                      <Avatar className="size-12">
                        <AvatarImage src={speaker.pictureUrl} className="object-cover" />
                        <AvatarFallback>
                          {speaker.fullname
                            ?.split(' ')
                            .map((n) => n.charAt(0))
                            .join('')
                            .toUpperCase()
                            .slice(0, 2) ?? '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p
                          className={
                            publicSession?.visualIdentity?.theme === 'DARK'
                              ? 'text-white'
                              : 'text-black'
                          }
                          style={{
                            fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
                          }}
                        >
                          {t('waitingRoom.publicPage.with')}
                          <span
                            className=" text-xl font-semibold"
                            style={{
                              fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
                            }}
                          >
                            {speaker.fullname}
                          </span>{' '}
                        </p>
                        <p
                          className={`text-sm ${publicSession?.visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'}`}
                          style={{
                            fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
                          }}
                        >
                          {speaker.job}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            {waitingRoomPage?.displayMainMedia !== false && waitingRoomPage?.mainImage && (
              <img
                src={waitingRoomPage.mainImage}
                alt="waiting-room-bg"
                className="w-full h-full object-cover sm:hidden rounded-[20px] "
              />
            )}
            {waitingRoomPage?.displayMainMedia !== false && waitingRoomPage?.mainVideoUrl && (
              <video
                src={waitingRoomPage?.mainVideoUrl}
                className="w-full h-full sm:hidden rounded-md "
                controls
                muted
                loop
              />
            )}
            {webinarIndex?.showIndex && webinarIndex?.items.length > 0 && (
              <div className=" w-full">
                <p
                  className={`text-sm pb-4 text-left ${publicSession?.visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'}`}
                  style={{
                    fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
                  }}
                >
                  {t('waitingRoom.indexTitle')}
                </p>
                <Accordion
                  type="single"
                  collapsible
                  defaultValue="item-1"
                  className="space-y-[10px]"
                >
                  {webinarIndex?.items.map((item: any, index: number) => (
                    <AccordionItem
                      key={item.title + index}
                      value={`item-${index + 1}`}
                      theme={publicSession?.visualIdentity?.theme as 'LIGHT' | 'DARK'}
                    >
                      <AccordionTrigger
                        chevronColor={publicSession?.visualIdentity?.accentColor}
                        hideChevron={!item.description || item.description === ''}
                        className={`${!item.description || item.description === '' ? 'pointer-events-none' : ''}items-start `}
                      >
                        <div className="flex items-start text-left gap-2">
                          <span
                            style={{
                              fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
                              color: publicSession?.visualIdentity?.accentColor,
                            }}
                          >
                            {index + 1}.
                          </span>{' '}
                          <span
                            style={{
                              fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
                              color:
                                publicSession?.visualIdentity?.theme === 'DARK' ? 'white' : 'black',
                            }}
                          >
                            {item.title}
                          </span>
                        </div>
                      </AccordionTrigger>
                      {item.description && item.description !== '' && (
                        <AccordionContent className="bg-transparent">
                          <span
                            style={{
                              fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
                              color:
                                publicSession?.visualIdentity?.theme === 'DARK' ? 'white' : 'black',
                            }}
                          >
                            {item.description}
                          </span>
                        </AccordionContent>
                      )}
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>
        </div>

        {/* right side */}
        <div className="w-full flex flex-1 flex-col gap-6 lg:sticky top-8 items-center">
          {/* logo welya */}
          <div className="w-full  hidden lg:flex items-center justify-end">
            <PoweredByLogo
              theme={publicSession?.visualIdentity?.theme as 'LIGHT' | 'DARK'}
              fontFamily={publicSession?.visualIdentity?.fontFamily}
              isNotLivePage={true}
            />
          </div>

          {/* time remaining */}
          <div className="w-full hidden lg:block ">{timeRemaining()}</div>

          {/* btn */}
          <div
            className={` w-full  gap-6 ${waitingRoomSurvey?.waitingRoomSurvey || waitingRoomQuiz?.waitingRoomQuiz ? 'flex flex-col sm:grid sm:grid-cols-3' : 'flex flex-col sm:grid sm:grid-cols-2'}`}
          >
            {/* btn connexion */}
            <div
              className="flex items-center lg:items-start xl:items-center flex-row lg:flex-col xl:flex-row gap-2 p-2 rounded-lg"
              style={{
                backgroundColor:
                  publicSession?.visualIdentity?.theme === 'LIGHT'
                    ? 'rgba(31, 31, 31, 0.03)'
                    : 'rgba(255, 255, 255, 0.1)',
              }}
            >
              <div>
                <div className="size-10 rounded-lg bg-black flex items-center justify-center">
                  {!connectionQuality && <Loader2 className="size-6 animate-spin text-white" />}
                  {connectionQuality && renderWifiIcon()}
                </div>
              </div>

              <div className="flex flex-col">
                <p
                  className="text-xs"
                  style={{
                    color: publicSession?.visualIdentity?.theme === 'DARK' ? 'white' : 'black',
                    fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
                  }}
                >
                  {t('waitingRoom.publicPage.connection.label')}
                </p>
                <p
                  className="text-sm"
                  style={{
                    fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
                    color: publicSession?.visualIdentity?.theme === 'DARK' ? 'white' : 'black',
                  }}
                >
                  {connectionQuality === 'high' && t('waitingRoom.publicPage.connection.strong')}
                  {connectionQuality === 'medium' && t('waitingRoom.publicPage.connection.medium')}
                  {connectionQuality === 'low' && t('waitingRoom.publicPage.connection.weak')}
                </p>
              </div>
            </div>

            {/* btn explore tool */}
            <button
              aria-label={t('waitingRoom.publicPage.aria.exploreTool')}
              title={t('waitingRoom.publicPage.aria.exploreTool')}
              className="flex items-center gap-2 p-2 text-left rounded-lg relative justify-between  cursor-pointer "
              style={{
                backgroundColor:
                  publicSession?.visualIdentity?.theme === 'LIGHT'
                    ? 'rgba(31, 31, 31, 0.03)'
                    : 'rgba(255, 255, 255, 0.1)',
                borderRadius: isToolsActive ? '10px 10px 0 0' : '10px',

                justifyContent: isToolsActive ? ' sm:center' : ' sm:flex-start',
              }}
              onClick={() => {
                setIsToolsActive(!isToolsActive);
                if (isSurveyActive) {
                  setIsSurveyActive(false);
                }
                if (isQuizActive) {
                  setIsQuizActive(false);
                }
              }}
            >
              <div className="flex items-center lg:items-start xl:items-center flex-row lg:flex-col xl:flex-row gap-2 w-full">
                <div className="shrink-0 size-10 rounded-lg bg-black flex items-center justify-center">
                  <IconFlash color={publicSession?.visualIdentity?.contrastColor} size={24} />
                </div>

                <p
                  className="text-sm"
                  style={{
                    fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
                    color: publicSession?.visualIdentity?.theme === 'DARK' ? 'white' : 'black',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: t('waitingRoom.survey.exploreTool'),
                  }}
                />
                {isToolsActive ? (
                  <Plus
                    className="size-6 transition-all rotate-45 duration-200 ml-auto sm:hidden"
                    style={{ color: publicSession?.visualIdentity?.contrastColor }}
                  />
                ) : (
                  <Plus
                    className="size-6 transition-all rotate-0 duration-200 ml-auto sm:hidden"
                    style={{ color: publicSession?.visualIdentity?.contrastColor }}
                  />
                )}
              </div>
              {isToolsActive && (
                <div
                  className="w-full z-0 h-6 absolute  left-0 -bottom-6 "
                  style={{
                    backgroundColor:
                      publicSession?.visualIdentity?.theme === 'LIGHT'
                        ? 'rgba(31, 31, 31, 0.03)'
                        : 'rgba(255, 255, 255, 0.1)',
                  }}
                >
                  {(waitingRoomSurvey?.waitingRoomSurvey || waitingRoomQuiz?.waitingRoomQuiz) && (
                    <div className="absolute hidden sm:block sm:bottom-0 sm:-right-6 sm:size-6 sm:overflow-hidden">
                      <CurvedCorner
                        theme={publicSession?.visualIdentity?.theme as 'LIGHT' | 'DARK'}
                        corner="bottom-left"
                      />
                    </div>
                  )}
                  <div className="absolute hidden sm:block sm:bottom-0 sm:-left-6 sm:size-6 sm:overflow-hidden">
                    <CurvedCorner
                      theme={publicSession?.visualIdentity?.theme as 'LIGHT' | 'DARK'}
                      corner="bottom-right"
                    />
                  </div>
                </div>
              )}
            </button>
            {isToolsActive && (
              <div
                style={{
                  backgroundColor:
                    publicSession?.visualIdentity?.theme === 'LIGHT'
                      ? 'rgba(31, 31, 31, 0.03)'
                      : 'rgba(255, 255, 255, 0.1)',
                }}
                className="w-full block sm:hidden p-4 h-full rounded-b-[20px]! rounded-ss-none! sm:rounded-ss-[20px]!"
              >
                {toolsContent()}
              </div>
            )}
            {/* btn quiz/survey */}
            {(waitingRoomSurvey?.waitingRoomSurvey || waitingRoomQuiz?.waitingRoomQuiz) && (
              <>
                <button
                  aria-label={t('waitingRoom.publicPage.aria.quizSurvey')}
                  title={t('waitingRoom.publicPage.aria.quizSurvey')}
                  className="flex items-center lg:items-start xl:items-center  gap-2 p-2 text-left lg:flex-col xl:flex-row lg:justify-start xl:justify-start rounded-lg relative cursor-pointer"
                  style={{
                    backgroundColor:
                      publicSession?.visualIdentity?.theme === 'LIGHT'
                        ? 'rgba(31, 31, 31, 0.03)'
                        : 'rgba(255, 255, 255, 0.1)',
                    borderRadius: isSurveyActive || isQuizActive ? '10px 10px 0 0' : '10px',
                  }}
                  onClick={() => {
                    const isSurvey = waitingRoomPage?.interactionType === 'SURVEY';
                    const isQuiz = waitingRoomPage?.interactionType === 'QUIZ';

                    if (isSurvey && hasWaitingRoomSurveyCompleted) {
                      return;
                    }
                    if (isQuiz && hasWaitingRoomQuizCompleted) {
                      return;
                    }

                    if (isSurvey) {
                      setIsSurveyActive(!isSurveyActive);
                      if (isQuizActive) {
                        setIsQuizActive(false);
                      }
                    } else if (isQuiz) {
                      setIsQuizActive(!isQuizActive);
                      if (isSurveyActive) {
                        setIsSurveyActive(false);
                      }
                    }

                    if (isToolsActive) {
                      setIsToolsActive(false);
                    }
                  }}
                >
                  {(waitingRoomPage?.interactionType === 'SURVEY' &&
                    hasWaitingRoomSurveyCompleted) ||
                  (waitingRoomPage?.interactionType === 'QUIZ' && hasWaitingRoomQuizCompleted) ? (
                    <>
                      <div className="size-10 shrink-0 rounded-lg flex items-center justify-center">
                        <Check
                          className="size-6 "
                          color={publicSession?.visualIdentity?.contrastColor}
                        />
                      </div>

                      <p
                        className="text-sm"
                        style={{
                          fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
                          color: '#9E9FA6',
                        }}
                        dangerouslySetInnerHTML={{
                          __html:
                            waitingRoomPage?.interactionType === 'SURVEY'
                              ? t('waitingRoom.survey.doSurvey')
                              : t('waitingRoom.publicPage.quiz.ctaDone'),
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <div className="size-10 shrink-0 rounded-lg bg-black flex items-center justify-center">
                        <IconNote color={publicSession?.visualIdentity?.contrastColor} size={24} />
                      </div>

                      <p
                        className="text-sm"
                        style={{
                          fontFamily: publicSession?.visualIdentity?.fontFamily || 'Poppins',
                          color:
                            publicSession?.visualIdentity?.theme === 'DARK' ? 'white' : 'black',
                        }}
                        dangerouslySetInnerHTML={{
                          __html:
                            waitingRoomPage?.interactionType === 'SURVEY'
                              ? t('waitingRoom.survey.doSurvey')
                              : t('waitingRoom.publicPage.quiz.cta'),
                        }}
                      />
                      {isSurveyActive || isQuizActive ? (
                        <Plus
                          className="size-6 transition-all rotate-45 duration-200 ml-auto sm:hidden"
                          style={{ color: publicSession?.visualIdentity?.contrastColor }}
                        />
                      ) : (
                        <Plus
                          className="size-6 transition-all duration-200 ml-auto sm:hidden"
                          style={{ color: publicSession?.visualIdentity?.contrastColor }}
                        />
                      )}
                    </>
                  )}

                  {(isSurveyActive || isQuizActive) && (
                    <div
                      className="w-full z-0 h-6  absolute  left-0 -bottom-6 "
                      style={{
                        backgroundColor:
                          publicSession?.visualIdentity?.theme === 'LIGHT'
                            ? 'rgba(31, 31, 31, 0.03)'
                            : 'rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <div className="absolute hidden sm:block bottom-0 -left-6 size-6  ">
                        <CurvedCorner
                          theme={publicSession?.visualIdentity?.theme as 'LIGHT' | 'DARK'}
                          corner="bottom-right"
                        />
                      </div>
                    </div>
                  )}
                </button>
                {(isSurveyActive || isQuizActive) && !isSmUp && (
                  <div
                    style={{
                      backgroundColor:
                        publicSession?.visualIdentity?.theme === 'LIGHT'
                          ? 'rgba(31, 31, 31, 0.03)'
                          : 'rgba(255, 255, 255, 0.1)',
                    }}
                    className="w-full block sm:hidden p-4 h-full rounded-b-[20px]! rounded-ss-none! sm:rounded-ss-[20px]!"
                  >
                    {waitingRoomSurvey?.waitingRoomSurvey ? (
                      <WaitingRoomSurveyForm
                        waitingRoomSurvey={waitingRoomSurvey}
                        visualIdentity={publicSession?.visualIdentity as WorkspaceVisualIdentity}
                        theme={publicSession?.visualIdentity?.theme as 'LIGHT' | 'DARK'}
                        token={token as string}
                        setIsSurveyActive={setIsSurveyActive}
                      />
                    ) : (
                      <WaitingRoomQuizForm
                        waitingRoomQuiz={waitingRoomQuiz!}
                        visualIdentity={publicSession?.visualIdentity as WorkspaceVisualIdentity}
                        theme={publicSession?.visualIdentity?.theme as 'LIGHT' | 'DARK'}
                        token={token as string}
                        setIsQuizActive={setIsQuizActive}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* content */}
          <div
            className={`w-full h-full hidden sm:block rounded-b-[20px]! ${isSurveyActive || isQuizActive ? 'rounded-se-none! rounded-ss-[20px]!' : 'rounded-se-[20px]! rounded-ss-[20px]!'}  ${isToolsActive || isSurveyActive || isQuizActive ? 'p-4' : ''} ${waitingRoomSurvey?.waitingRoomSurvey || waitingRoomQuiz?.waitingRoomQuiz ? 'rounded-se-[20px]!' : 'rounded-se-none!'}`}
            style={{
              backgroundColor:
                publicSession?.visualIdentity?.theme === 'LIGHT'
                  ? 'rgba(31, 31, 31, 0.03)'
                  : 'rgba(255, 255, 255, 0.1)',
            }}
          >
            {isToolsActive === false &&
              isSurveyActive === false &&
              isQuizActive === false &&
              waitingRoomPage?.displayMainMedia !== false &&
              waitingRoomPage?.mainImage && (
                <img
                  src={waitingRoomPage.mainImage}
                  alt="waiting-room-bg"
                  className="w-full h-auto object-cover hidden sm:block rounded-[20px] "
                />
              )}
            {isToolsActive === false &&
              isSurveyActive === false &&
              isQuizActive === false &&
              waitingRoomPage?.displayMainMedia !== false &&
              waitingRoomPage?.mainVideoUrl && (
                <video
                  src={waitingRoomPage?.mainVideoUrl}
                  className="w-full h-full hidden sm:block rounded-md "
                  controls
                  muted
                  loop
                />
              )}
            {isToolsActive && <>{toolsContent()}</>}

            {isSmUp && isSurveyActive && waitingRoomSurvey?.waitingRoomSurvey && token && (
              <WaitingRoomSurveyForm
                waitingRoomSurvey={waitingRoomSurvey}
                visualIdentity={publicSession?.visualIdentity as WorkspaceVisualIdentity}
                theme={publicSession?.visualIdentity?.theme as 'LIGHT' | 'DARK'}
                token={token}
                setIsSurveyActive={setIsSurveyActive}
              />
            )}

            {isSmUp && isQuizActive && waitingRoomQuiz?.waitingRoomQuiz && token && (
              <WaitingRoomQuizForm
                waitingRoomQuiz={waitingRoomQuiz}
                visualIdentity={publicSession?.visualIdentity as WorkspaceVisualIdentity}
                theme={publicSession?.visualIdentity?.theme as 'LIGHT' | 'DARK'}
                token={token}
                setIsQuizActive={setIsQuizActive}
              />
            )}
          </div>
          <div className="w-full  flex lg:hidden items-center justify-start">
            <PoweredByLogo
              theme={publicSession?.visualIdentity?.theme as 'LIGHT' | 'DARK'}
              fontFamily={publicSession?.visualIdentity?.fontFamily}
              isNotLivePage={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
